// User authentication types
export interface AdminUser {
	id: string;
	username: string;
	email: string;
	type: 'admin' | 'super-admin';
	lastLogin?: string;
	createdAt: string;
	isAdmin: true;
	firstName?: string;
	lastName?: string;
}

export interface RegularUser {
	id: string;
	firstName: string;
	lastName: string;
	email: string;
	type: UserType;
	institution: string;
	lastLogin?: string;
	createdAt: string;
}

export type AppUser = AdminUser | RegularUser;

export interface LoginCredentials {
	username: string;
	password: string;
}

export interface LoginResponse {
	token: string;
	user: AppUser;
	expiresIn: number;
}

// Survey and report management types
export interface SubmittedSurvey {
	id: string;
	age?: number;
	targetGroup: 'kids' | 'adults' | 'health-workers';
	riskScore: number;
	potentialTrafficking: boolean;
	submittedAt: string;
	answers: SurveyAnswer[];
	report?: SubmittedReport;
	riskLevel: 'low' | 'medium' | 'high';
}

export interface SurveyAnswer {
	question: any;
	answer: string | boolean | number;
}

export interface SubmittedReport {
	id: string;
	surveyId: string;
	riskScore: number;
	formData: ReportFormData;
	submittedAt: string;
	status: 'pending' | 'reviewed' | 'processed';
	reviewedBy?: string;
	reviewedAt?: string;
	notes?: string;
	enrolled?: boolean;
	decision?: boolean;
	reason?: string;
	createdBy: string;
	completedBy?: string;
}

// Form data from the report modal
export interface ReportFormData {
	name: string;
	surname: string;
	dateOfBirth: string;
	address: string;
	country: string;
	contact: string;
	gender: string;
	maritalStatus: string;
	stayingWithType: string;
	placeWhereLives: string;
	locationAddress: string;
	specialStatus: string;
	healthStatus: string;
	exploitationType: string;
	exploitationPhase: string;
	explanation: string;
	additionalNotes: string;
	neededSupport: string;
	neededSupportDetails: string;
	givenSupport: string;
	givenSupportDetails: string;
	// Deprecated fields
	socialStatus: string;
	servicesNeeded: string;
	requiredServices: string;
}

// API response types
export interface AdminDashboardData {
	totalSurveys: number;
	totalReports: number;
	highRiskCount: number;
	recentSubmissions: SubmittedSurvey[];
	statistics: {
		surveysToday: number;
		reportsToday: number;
		averageRiskScore: number;
		potentialTraffickingCount: number;
	};
}

export interface PaginatedResponse<T> {
	data: T[];
	total: number;
	page: number;
	pageSize: number;
	totalPages: number;
}

export interface SurveyFilters {
	startDate?: string;
	endDate?: string;
	riskLevel?: 'low' | 'medium' | 'high';
	targetGroup?: 'kids' | 'adults' | 'health-workers';
	hasReport?: boolean;
	potentialTrafficking?: boolean;
}

export interface ReportFilters {
	startDate?: string;
	endDate?: string;
	status?: 'pending' | 'reviewed' | 'processed';
	riskLevel?: 'low' | 'medium' | 'high';
}

// Auth context type
export interface AuthContextType {
	user: AppUser | null;
	token: string | null;
	login: (credentials: LoginCredentials) => Promise<void>;
	logout: () => void;
	isLoading: boolean;
	isAuthenticated: boolean;
	isAdmin: boolean;
}

// User management types
export enum UserType {
	ADMIN = 'admin',
	SOCIAL = 'social',
	HEALTH = 'health',
}

export interface User {
	id: string;
	firstName: string;
	lastName: string;
	phoneNumber: string;
	email: string;
	password?: string; // Optional for responses (security)
	type: UserType;
	institution: string;
	isActive: boolean;
	emailVerified: boolean;
	verificationToken?: string;
	verificationTokenExpires?: string;
	passwordSetAt?: string;
	createdAt: string;
	updatedAt: string;
	lastLoginAt?: string;
}

export interface CreateUserRequest {
	firstName: string;
	lastName: string;
	phoneNumber: string;
	email: string;
	type: UserType;
	institution: string;
}

export interface UpdateUserRequest {
	firstName: string;
	lastName: string;
	phoneNumber: string;
	email: string;
	type: UserType;
	institution: string;
	isActive: boolean;
}

export interface UserFilters {
	type?: UserType;
	institution?: string;
	isActive?: boolean;
	search?: string;
}

// User submissions and reports types
export interface UserSubmission {
	id: string;
	surveyId: string;
	riskScore: number;
	submittedAt: string;
	formData: any;
	// Computed properties for UI
	type?: 'survey' | 'report';
	status?: 'pending' | 'reviewed' | 'processed';
	riskLevel?: 'low' | 'medium' | 'high';
	title?: string;
	description?: string;
}

export interface UserProfile {
	firstName: string;
	lastName: string;
	email: string;
	phoneNumber: string;
	institution: string;
	type: UserType;
}

// SMTP Configuration types
export interface SMTPConfig {
	id: number;
	name: string;
	host: string;
	port: number;
	username: string;
	// password is excluded from responses for security
	from_email: string;
	from_name?: string;
	secure: boolean;
	require_tls: boolean;
	is_active: boolean;
	is_default: boolean;
	created_at: string;
	updated_at: string;
}

export interface SMTPConfigForm {
	name: string;
	host: string;
	port: number;
	username: string;
	password: string;
	from_email: string;
	from_name?: string;
	secure: boolean;
	require_tls: boolean;
	is_active: boolean;
	is_default: boolean;
}

export interface SMTPConfigCreate {
	name: string;
	host: string;
	port: number;
	username: string;
	password: string;
	from_email: string;
	from_name?: string;
	secure: boolean;
	require_tls: boolean;
	is_active: boolean;
	is_default: boolean;
}

export interface SMTPConfigUpdate {
	name?: string;
	host?: string;
	port?: number;
	username?: string;
	password?: string;
	from_email?: string;
	from_name?: string;
	secure?: boolean;
	require_tls?: boolean;
	is_active?: boolean;
	is_default?: boolean;
}

export interface ConnectionTestResponse {
	success: boolean;
	error?: string;
}

export interface SendEmailDto {
	to: string;
	subject: string;
	text?: string;
	html?: string;
	smtp_config_id?: number;
}

export interface EmailResponse {
	success: boolean;
	messageId?: string;
	error?: string;
}

// SMTP Provider Templates
export interface SMTPProviderTemplate {
	name: string;
	displayName: string;
	host: string;
	port: number;
	secure: boolean;
	require_tls: boolean;
	usernameLabel: string;
	passwordLabel: string;
	instructions: string;
}

export const SMTP_PROVIDER_TEMPLATES: SMTPProviderTemplate[] = [
	{
		name: 'gmail',
		displayName: 'Gmail',
		host: 'smtp.gmail.com',
		port: 587,
		secure: false,
		require_tls: true,
		usernameLabel: 'Gmail address',
		passwordLabel: 'App password (not regular password)',
		instructions: 'Use Gmail app password. Enable 2FA and generate app password in Google Account settings.',
	},
	{
		name: 'office365',
		displayName: 'Office 365',
		host: 'smtp.office365.com',
		port: 587,
		secure: false,
		require_tls: true,
		usernameLabel: 'Office 365 email',
		passwordLabel: 'Office 365 password',
		instructions: 'Use your Office 365 email and password. Modern authentication may be required.',
	},
	{
		name: 'amazon-ses',
		displayName: 'Amazon SES',
		host: 'email-smtp.us-east-1.amazonaws.com',
		port: 587,
		secure: false,
		require_tls: true,
		usernameLabel: 'SMTP username (IAM access key)',
		passwordLabel: 'SMTP password (IAM secret key)',
		instructions: 'Use IAM credentials with SES permissions. Verify sender email/domain in SES console.',
	},
	{
		name: 'sendgrid',
		displayName: 'SendGrid',
		host: 'smtp.sendgrid.net',
		port: 587,
		secure: false,
		require_tls: true,
		usernameLabel: 'Username (always "apikey")',
		passwordLabel: 'SendGrid API Key',
		instructions: 'Username must be "apikey". Use your SendGrid API key as password.',
	},
	{
		name: 'mailgun',
		displayName: 'Mailgun',
		host: 'smtp.mailgun.org',
		port: 587,
		secure: false,
		require_tls: true,
		usernameLabel: 'Mailgun SMTP username',
		passwordLabel: 'Mailgun SMTP password',
		instructions: 'Use your Mailgun domain SMTP credentials. Verify your domain in Mailgun dashboard.',
	},
];

// User email verification types
export interface VerificationStatus {
	emailVerified: boolean;
	hasVerificationToken: boolean;
	tokenExpires?: string;
	passwordSet: boolean;
}

export interface ResendVerificationResponse {
	success: boolean;
	message: string;
}

export interface VerifyEmailRequest {
	token: string;
	password: string;
}

export interface SetPasswordRequest {
	token: string;
	password: string;
}

export interface TokenValidationResponse {
	valid: boolean;
	user?: {
		id: string;
		email: string;
		firstName: string;
		lastName: string;
		emailVerified: boolean;
	};
}

// Password reset types
export interface ResetPasswordRequest {
	token: string;
	newPassword: string;
}

export interface ResetPasswordResponse {
	success: boolean;
	message: string;
}
